import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CommentsInput, GeneratorStepProps as Props, InstallationsFieldKeys } from '../types'
import FormButtonGroup from 'components/form-fields/form-button-group/FormButtonGroup'
import Button from 'components/button/Button'
import { ButtonTypes } from 'components/button/types'
import { useForm } from 'react-hook-form'
import { ProducerContactSteps } from 'features/producer-contact/types'
import LoadingDots from 'components/loading-dots/LoadingDots'
import { producerRequest } from 'api/producer'
import dayjs from 'dayjs'

const Comments: FC<Props> = ({ formInput, handlePrevious, setCurrentStep }) => {
  // Local state
  const [sending, setSending] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['generator', 'common'])

  // React Hook Form
  const { register, handleSubmit, getValues } = useForm<CommentsInput>({
    mode: 'onBlur',
    defaultValues: formInput?.[ProducerContactSteps.COMMENTS] ?? {}
  })

  /**
   * Handles the submit after validation by React Hook Form
   *
   * @param {CommentsInput} data
   */
  const onSubmit = async (data: CommentsInput) => {
    setSending(true)
    setError(false)

    // Prepare data & send it to the API
    const requestData = {
      companyData: formInput.companyData,
      installations: formInput.installations.map((installation) => ({
        ...installation,
        ...(installation.expirationCurrentContract && {
          expirationCurrentContract: dayjs(
            `${installation.expirationCurrentContract.year}-${installation.expirationCurrentContract.month}-${installation.expirationCurrentContract.day}`
          ).toISOString()
        }),
        producedVolume: installation[InstallationsFieldKeys.PRODUCED_VOLUME] || null,
        injectedVolume: installation[InstallationsFieldKeys.INJECTED_VOLUME] || null,
        eanInjection: installation[InstallationsFieldKeys.EAN_INJECTION] || null
      })),
      comments: data.questionsComments
    }

    const sent = await producerRequest(requestData)

    if (sent) {
      setCurrentStep(ProducerContactSteps.CONFIRMATION)
    } else {
      setError(true)
    }
    setSending(false)
  }

  /**
   * Handles the previous click
   */
  const onPreviousClick = async () => {
    handlePrevious(getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h3 className="margin-bottom-m">{t('progressCommentsData', 'Opmerkingen')}</h3>

      <div className="form-row">
        <div className="form-input">
          <textarea rows={5} placeholder={t('optional', 'Optioneel')} {...register('questionsComments')} />
        </div>
      </div>

      <FormButtonGroup>
        <Button type={ButtonTypes.TERNARY} onClick={onPreviousClick} isNotSubmit isDisabled={sending || error}>
          {t('previousStep', 'Vorige stap')}
        </Button>

        <Button isDisabled={sending || error}>{sending ? <LoadingDots /> : t('sendRequest', 'Aanvraag verzenden')}</Button>
      </FormButtonGroup>

      {!sending && error && (
        <span className="is-red is-block margin-bottom-m">
          {t('common:error', 'Er is iets misgelopen, contacteer onze helpdesk op 02 899 33 00.')}
        </span>
      )}
    </form>
  )
}

export default Comments
